import { useContext, useState } from 'react';
import { Alert, Button, InputNumber, Modal, Select, message } from 'antd';
import ProfileContext from 'services/profileContext';
import DomainContext from 'services/domainContext';
import { allocatePoints } from 'services/api/domains';
import { handleDomainAccessError } from 'utils/domainAccessErrors';

const ModalPointsToAllocate = () => {
  const { profile, updateProfile } = useContext(ProfileContext);
  const { domains, updateDomains } = useContext(DomainContext);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [amount, setAmount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOk = async () => {
    if (!selectedDomain) {
      message.error('Please select a domain');
      return;
    }
    if (!amount || amount < 1 || amount > profile.pointsToAllocate) {
      message.error(`Enter between 1 and ${profile.pointsToAllocate} points`);
      return;
    }
    setIsLoading(true);
    const domainName = domains.find(
      (domain) => domain._id === selectedDomain
    )?.name;
    allocatePoints({
      amount,
      name: domainName,
    })
      .then(() => {
        updateDomains();
        updateProfile();
        message.success('Points allocated successfully');
        setIsModalOpen(false);
        setSelectedDomain(null);
      })
      .catch((error) => {
        if (handleDomainAccessError(error, {
          updateProfile,
          onNotApproved: () => setIsModalOpen(false),
        })) {
          return;
        }
        if (error.response?.data?.error === 'NOT_ENOUGH_POINTS_TO_ALLOCATE') {
          message.error('You no longer have that many points, please try again');
          updateProfile();
          return;
        }
        message.error(
          error.response?.data?.error ||
            'Failed to allocate points, please try again or contact support'
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  if (!profile?.pointsToAllocate) {
    return null;
  }
  return (
    <>
      <Alert
        showIcon
        message={`You have ${profile.pointsToAllocate} points to allocate to a domain!`}
        type="warning"
        style={{ marginBottom: '12px' }}
        action={
          <Button
            size="small"
            type="primary"
            onClick={() => {
              setAmount(profile.pointsToAllocate);
              setIsModalOpen(true);
            }}
          >
            Allocate Points
          </Button>
        }
      />
      <Modal
        title="Allocate Points"
        open={isModalOpen}
        onOk={handleOk}
        okText={`Allocate ${amount || 0} points`}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedDomain(null);
        }}
        confirmLoading={isLoading}
      >
        <p>You have {profile.pointsToAllocate} points to allocate!</p>
        <p>Choose a domain and how many points to give it:</p>
        <Select
          style={{ width: '100%' }}
          placeholder="Select a domain"
          onChange={(value) => setSelectedDomain(value)}
          value={selectedDomain}
        >
          {domains?.map((domain) => (
            <Select.Option key={domain._id} value={domain._id}>
              {domain.name}
            </Select.Option>
          ))}
        </Select>
        <InputNumber
          style={{ width: '100%', marginTop: '12px' }}
          min={1}
          max={profile.pointsToAllocate}
          precision={0}
          value={amount}
          onChange={(value) => setAmount(value)}
          addonAfter={`of ${profile.pointsToAllocate}`}
        />
      </Modal>
    </>
  );
};

export default ModalPointsToAllocate;
